import { WORKSPACE_HEADER } from '@mentorivo/shared';

import { isAbortError, isOffline, reportOffline, reportOnline } from '@/lib/connection';
import { endSession, sessionEnding } from '@/lib/session-guard';
import { workspaceFromPath } from '@/lib/workspace-route';

/// Backend istemcisi.
///
/// ---------------------------------------------------------------------------
/// İki farklı adres, tek istemci
/// ---------------------------------------------------------------------------
/// API kendi alan adında yayında (api.mentorivo.com). Ama sunucu tarafı (SSR,
/// route handler) oraya gitmemeli: istek nginx'e uğrar, TLS el sıkışması yapar
/// ve vhost'taki IP kısıtına takılır. Sunucudan çağrı doğrudan 127.0.0.1:3040'a
/// gider — hem daha hızlı hem de erişim listesine bağlı değil.
///
///   tarayıcı → https://api.mentorivo.com   (NEXT_PUBLIC_API_URL)
///   sunucu   → http://127.0.0.1:3040       (API_INTERNAL_URL)
///
/// Çerezler `.mentorivo.com` alan adına yazılıyor; mentorivo.com ile
/// api.mentorivo.com aynı kayıtlı alan adında olduğu için tarayıcı bunu
/// "same-site" sayıyor ve SameSite=Lax çerezler isteğe ekleniyor.
/// `credentials: 'include'` yine şart — farklı origin'e giden istekte
/// tarayıcı çerezi kendiliğinden koymuyor.

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
  /// Varsayılanı ezmek gerekirse (test, farklı ortam).
  baseUrl?: string;
};

/// Tarayıcıda mı sunucuda mı çalıştığımıza göre kök adres.
/// `NEXT_PUBLIC_*` derleme anında gömülür, diğeri yalnızca sunucuda okunur.
///
/// DIŞA AÇIK, çünkü tek bir tüketici bu istemciyi kullanamıyor: ajan sohbeti
/// SSE akışını `fetch` ile kendi okuyor (`lib/agent-api.ts`) — gövde bir
/// `ReadableStream` ve `request()` her yanıtı `json()` ile tüketiyor. Kuralın
/// ikinci bir kopyası yazılsaydı, adres bir gün değiştiğinde biri güncellenip
/// diğeri eski kalırdı.
export function apiBaseUrl(): string {
  if (typeof window !== 'undefined') {
    return process.env.NEXT_PUBLIC_API_URL ?? '';
  }
  return process.env.API_INTERNAL_URL ?? 'http://127.0.0.1:3040';
}

/// Uçuştaki yenileme. Paralel on 401 tek bir `/auth/refresh` yapsın diye
/// paylaşılıyor; jeton rotasyonlu olduğu için ikinci eşzamanlı çağrı
/// birincinin daha yeni jetonunu geçersiz kılardı.
let refreshInFlight: Promise<RefreshOutcome> | null = null;

/**
 * Yenilemenin üç ayrı sonucu — ve üçünün ayrı durması ŞART.
 *
 * Eski sürüm `boolean` döndürüyordu: "yenilenemedi" ile "sunucuya
 * ulaşılamadı" aynı değerdi. Sonucu, internet kopukken gelen bir 401'in
 * kullanıcıyı giriş ekranına atmasıydı — oysa oturumu gayet diri, yalnızca
 * hat yok. Ölü bir oturumla geçici bir kopukluk birbirine karıştırılamaz.
 */
type RefreshOutcome =
  /// Yenilendi, istek tekrarlanabilir.
  | 'ok'
  /// Sunucu AÇIKÇA reddetti: oturum gerçekten bitmiş.
  | 'rejected'
  /// Cevap alınamadı (ağ hatası, sunucu kapalı). Oturum hakkında bir şey bilmiyoruz.
  | 'unreachable';

/**
 * Yenilemeyi SEKMELER ARASI tekilleştirir.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Neden kilit gerekiyor
 * ═══════════════════════════════════════════════════════════════════════════
 * `refreshInFlight` yalnızca AYNI sekmedeki paralel istekleri birleştiriyor.
 * İki sekme aynı anda 401 aldığında ikisi de yeniliyor ve refresh jetonu
 * rotasyonlu: birinci yenileme jetonu değiştiriyor, ikincisi elindeki artık
 * eski jetonla geliyor. Sunucu tarafındaki yeniden kullanım tespiti bunu
 * (kısa bir pencere dışında) hırsızlık sayıp OTURUMU KAPATIYOR — yani
 * kullanıcının iki sekme açmış olması onu dışarı atıyordu.
 *
 * Web Locks aynı origin'deki tüm sekmelerde geçerli: ikinci sekme birincinin
 * bitmesini bekliyor ve sırası geldiğinde ARTIK TAZE olan çerezle gidiyor.
 * Yani yarış hiç doğmuyor; sunucudaki pencere ikinci savunma olarak kalıyor.
 *
 * API yoksa (eski tarayıcı, güvensiz bağlam) işlev kilitsiz çalışıyor —
 * davranış eskisi gibi, sunucu penceresi devrede.
 */
function withRefreshLock<T>(run: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return run();
  return locks.request('mentorivo-refresh', run);
}

function refreshSession(baseUrl: string): Promise<RefreshOutcome> {
  refreshInFlight ??= withRefreshLock(async (): Promise<RefreshOutcome> => {
    try {
      const response = await fetch(`${baseUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
      });
      if (response.ok) {
        reportOnline();
        return 'ok';
      }
      /* 5xx ve 429 "oturum bitti" DEĞİL: sunucu o an cevap veremiyor.
         Kullanıcıyı bunun için dışarı atmak, API'nin her yeniden
         başlatılmasında herkesi giriş ekranına göndermek olurdu. */
      return response.status === 401 || response.status === 403 ? 'rejected' : 'unreachable';
    } catch {
      reportOffline();
      return 'unreachable';
    }
  }).finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/**
 * Dışa açık yenileme — istek katmanını kullanmayan tüketiciler için.
 *
 * Ajan sohbeti akışı (`lib/agent-api.ts`) gövdeyi kendi okuduğu için
 * `request()`ten geçmiyor; 401 aldığında yenilemeyi buradan çağırıyor ki
 * kural ikinci kez yazılmasın.
 */
export function ensureFreshSession(): Promise<RefreshOutcome> {
  return refreshSession(apiBaseUrl());
}

/**
 * 🔴 SÜRESİ DOLAN OTURUM — 401'de bir kez tazele, tutmazsa GİRİŞE AT.
 *
 * `mentorivo_at` 15 dakika, `mentorivo_rt` atıl kalma sınırı kadar yaşıyor.
 * Sayfa gezinmesinde tazelemeyi `proxy.ts` yapıyor, ama TARAYICIDAN atılan
 * `fetch` o katmandan geçmiyor: panel tek bir ekranda 15 dakika açık
 * kaldığında her çağrı 401 alıyordu ve arayüzde bunun karşılığı yoktu.
 *
 * Ölçülen sonuç: ürün analizi sunucuda 76 saniyede BİTTİ, ekran 10 dakikadan
 * uzun süre "okunuyor" dedi. Yoklama her turda 401 aldı, `catch` turu yuttu,
 * kullanıcı bekledi (`use-product-vision.ts` → yoklama).
 *
 * Tekrar GÜVENLİ: 401 muhafızdan dönüyor, yani istek İŞLENMEDEN reddedildi;
 * tekrarlanan bir `POST` ikinci bir yan etki üretmiyor.
 *
 * ---------------------------------------------------------------------------
 * ⚠️ Burada bir karar DEĞİŞTİ (17 Eyl 2026)
 * ---------------------------------------------------------------------------
 * Eski hâli şunu diyordu: *"Buradan /login'e yönlendirme yok; düşük katmanın
 * kullanıcıyı sayfadan atması, formda yazılmış bir şeyi çöpe atmak olur."*
 * Niyet doğruydu, sonucu yanlıştı: oturumu ölen kullanıcı sayfada KALIYOR ve
 * ekranda anlamsız hatalar biriktiriyordu. Yazdığı şey zaten kaydedilemezdi —
 * tek farkı, kaydedilemediğini öğrenmesinin dakikalar sürmesiydi.
 *
 * Yeni kural dar ve kesin: yalnızca **yenileme AÇIKÇA reddedildiğinde** ve
 * yalnızca **sunucu `SESSION_EXPIRED` dediğinde** çıkış yapılıyor.
 *
 *   • Yanlış giriş kodunun 401'i (`CODE_INVALID`) buraya girmiyor — giriş
 *     formu kendi hatasını göstermeye devam ediyor.
 *   • Ağ hatası buraya girmiyor: cevap alamadıysak oturum hakkında hiçbir şey
 *     bilmiyoruz demektir (bkz. `RefreshOutcome`).
 *   • Kopukken hiç girmiyor: giriş ekranına gitmek de bir ağ isteği ve kopuk
 *     hatta boş sayfa demek. O durumda bağlantı katmanı devrede.
 */
/**
 * 🔴 TARAYICIDAN GİDEN İSTEK ETKİN ÇALIŞMA ALANINI TAŞIR.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden çerez yetmiyor — iki sekme sorunu
 * ═══════════════════════════════════════════════════════════════════════════
 * API etkin alanı başlık → çerez sırasıyla okuyor. Başlık gönderilmediğinde
 * çereze düşüyor ve çerez TARAYICI BAŞINA TEK: kullanıcı A sekmesinde
 * Blesyum'a, B sekmesinde Bimarka'ya bakarken, B'de bastığı "duraklat"
 * düğmesi çerezdeki alana giderdi. Guard kimliği doğruladığı için sonuç
 * güvenli ama ANLAŞILMAZ olurdu: ekranda gördüğü kampanya için 404.
 *
 * Alan adresin kendisinde (`/tr/dashboard/w/blesyum/…`) ve adres sekmeye
 * özel — bu yüzden kaynak olarak `location.pathname` kullanılıyor. React
 * bağlamından okumak da mümkündü ama bu istemciyi React DIŞINDAN çağıran
 * yerler var (ajan SSE akışı `lib/agent-api.ts`); adres hepsinde geçerli.
 *
 * Sunucu tarafında `location` yok ve gerek de yok: orada başlığı
 * `serverApiHeaders()` proxy'nin yazdığı istek başlığından üretiyor.
 */
function browserWorkspaceHeader(): Record<string, string> {
  if (typeof window === 'undefined') return {};

  // Locale öneki soyuluyor: `/tr/dashboard/w/blesyum` → `/dashboard/w/blesyum`.
  // Önek listesi `routing.locales` ile aynı olmak zorunda değil — ilk segment
  // iki harfliyse soyuluyor, çünkü `workspaceFromPath` kalıbı tutmayan her
  // şeyi zaten eliyor.
  const path = window.location.pathname.replace(/^\/[a-z]{2}(?=\/|$)/, '');
  const slug = workspaceFromPath(path);
  return slug ? { [WORKSPACE_HEADER]: slug } : {};
}

const inBrowser = (): boolean => typeof window !== 'undefined';

/**
 * Arayüzün dili, `Accept-Language` başlığında (30 Eyl 2026, 13 dil).
 *
 * API'deki dil modeli çağrıları (kreatif ve kampanya direktörü) cevap dilini
 * bu başlıktan seçiyor. Tarayıcının KENDİ başlığı ise cihazın tercihi: dil
 * menüsünden Almancayı seçen ama tarayıcısı İngilizce olan kişi, Almanca
 * ekranda İngilizce cevap alıyordu. Kaynak `<html lang>` — kök layout onu
 * rota dilinden basıyor, ekranın gerçekten konuştuğu dil o.
 *
 * `Accept-Language` CORS'ta serbest başlık: ön kontrol (preflight) isteği
 * doğurmuyor, API'nin CORS ayarına dokunmak gerekmiyor.
 */
function uiLanguageHeader(): Record<string, string> {
  if (!inBrowser()) return {};
  const lang = document.documentElement.lang;
  return lang ? { 'Accept-Language': lang } : {};
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
  /// İç kullanım: yenilemeden sonraki tekrar bir daha yenilemesin.
  allowRefresh = true,
): Promise<T> {
  const { body, baseUrl = apiBaseUrl(), headers, ...rest } = options;

  /* Kopuk hatta istek AÇMIYORUZ. Tarayıcı zaten ~30 saniye deneyip patlıyor;
     o sırada ekranda dönen bir çark ve arka planda birikmiş yoklamalar oluyor.
     Hemen aynı hatayı üretmek, bekleyişin tamamını siliyor. Hata TÜRÜ bilerek
     ağ hatasıyla aynı (`TypeError`): çağıran otuz yerde `instanceof ApiError`
     ile dallanıyor ve kopukluk oraya girmemeli. */
  if (inBrowser() && isOffline()) {
    throw new TypeError('Bağlantı yok — istek gönderilmedi');
  }

  /* Çıkış yönlendirmesi başladıysa yeni istek açmıyoruz. Tam sayfa gezinmesi
     başlamış olsa bile tarayıcı sayfayı hemen boşaltmıyor; o aradaki birkaç
     yüz milisaniyede yoklama döngüleri ölü bir oturumla istek atmaya devam
     eder ve her biri yeni bir 401 üretirdi. */
  if (inBrowser() && sessionEnding()) {
    throw new TypeError('Oturum kapanıyor — istek gönderilmedi');
  }

  let response: Response;
  try {
    response = await fetch(`${baseUrl}${path}`, {
      ...rest,
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...browserWorkspaceHeader(),
        ...uiLanguageHeader(),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: 'include',
    });
  } catch (error) {
    /* Ağ seviyesinde patladı. Bu, elimizdeki en dürüst kopukluk kanıtı:
       gerçekten deneyip ulaşamadık. Hata olduğu gibi yukarı çıkıyor —
       sarmalamak, çağıranların `ApiError` ayrımını bozardı.

       İPTAL hariç: isteği biz kestik, hat hakkında hiçbir şey öğrenmedik
       (gerekçe `isAbortError`). */
    if (inBrowser() && !isAbortError(error)) reportOffline();
    throw error;
  }

  if (inBrowser()) reportOnline();

  /* Yalnız TARAYICIDA: sunucu tarafında yenileme `Set-Cookie` yazmayı
     gerektiriyor ve sunucu bileşeni yanıt başlığı yazamıyor — orada işi
     `proxy.ts` yapıyor. Gövde henüz okunmadı, yani yenileme tutmazsa aşağıdaki
     hata yolu bozulmadan çalışıyor. */
  let refreshOutcome: RefreshOutcome | null = null;
  if (response.status === 401 && allowRefresh && inBrowser() && !path.startsWith('/auth/refresh')) {
    refreshOutcome = await refreshSession(baseUrl);
    if (refreshOutcome === 'ok') return request<T>(path, options, false);
  }

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    // NestJS hata gövdesi: { statusCode, message, error }
    const p = payload as { message?: string | string[]; code?: string } | null;
    const message = Array.isArray(p?.message)
      ? p.message.join(', ')
      : (p?.message ?? response.statusText);

    /* Oturum gerçekten bitti mi — üç koşulun ÜÇÜ birden. Gerekçe yukarıdaki
       blokta; tek tek hangisinin neyi elediği orada yazılı. */
    if (
      inBrowser() &&
      response.status === 401 &&
      p?.code === 'SESSION_EXPIRED' &&
      refreshOutcome === 'rejected' &&
      !isOffline()
    ) {
      endSession('expired');
    }

    throw new ApiError(response.status, message, p?.code, payload);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
